import { useStandardButtonStyles } from "../../../styles/standardButtons";
import { toast } from "react-toastify";
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, Typography, Box,
  Grid, Divider, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import { i18n } from "../../../translate/i18n";

// [Plan 904] Modal de edición: Botones Interactivos META API
// standard button hook
const FlowBuilderInteractiveButtonModal = ({ node, onClose, onSave }) => {
  const standardClasses = useStandardButtonStyles();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [buttons, setButtons] = useState([{ id: "btn_1", text: "", varName: "", varValue: "" }]);
  const [ctaUrl, setCtaUrl] = useState({ label: "", url: "" });
  const [ctaPhone, setCtaPhone] = useState({ label: "", phone: "" });
  const [antiBan, setAntiBan] = useState(false);

  useEffect(() => {
    if (node && node.data) {
      setName(node.data.name || "");
      setBody(node.data.body || "");
      setButtons(node.data.buttons && node.data.buttons.length > 0
        ? node.data.buttons
        : [{ id: "btn_1", text: "" }]);
      setCtaUrl(node.data.ctaUrl || { label: "", url: "" });
      setCtaPhone(node.data.ctaPhone || { label: "", phone: "" });
      setAntiBan(!!node.data.antiBan);
    }
  }, [node]);

  const handleSave = () => {
    // Regenerar IDs basados en índice para consistencia con el backend
    const normalizedButtons = buttons.map((btn, idx) => ({
      ...btn,
      id: btn.id || `btn_${idx + 1}`
    }));
    onSave({ name, body, buttons: normalizedButtons, ctaUrl, ctaPhone, antiBan });
  };

  const addButton = () => {
    if (buttons.length >= 3) return;
    const newId = `btn_${buttons.length + 1}`;
    setButtons(prev => [...prev, { id: newId, text: "", varName: "", varValue: "" }]);
  };

  const removeButton = (idx) => {
    setButtons(prev => prev.filter((_, i) => i !== idx));
  };

  const updateButton = (idx, text) => {
    setButtons(prev => prev.map((btn, i) => i === idx ? { ...btn, text: text.substring(0, 20) } : btn));
  };

  const updateButtonVar = (idx, field, value) => {
    setButtons(prev => prev.map((btn, i) => i === idx ? { ...btn, [field]: value } : btn));
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <span style={{ fontSize: 18 }}>🔒</span>
          <Typography variant="h6" color="primary" style={{ fontWeight: 700 }}>
            {i18n.t("flowBuilder.interactiveButtons.editTitle")}
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("flowBuilder.interactiveButtons.subtitle")}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Nombre del nodo */}
        <TextField
          label={i18n.t("flowBuilder.interactiveButtons.nodeName")}
          value={name}
          onChange={e => setName(e.target.value)}
          variant="outlined"
          margin="dense"
          fullWidth
          style={{ marginBottom: 16 }}
        />

        {/* Cuerpo */}
        <TextField
          label={i18n.t("flowBuilder.interactiveButtons.messageBody")}
          value={body}
          onChange={e => setBody(e.target.value)}
          variant="outlined"
          margin="dense"
          fullWidth
          multiline
          rows={3}
          style={{ marginBottom: 16 }}
        />

        {/* Botones de Respuesta Rápida */}
        <Typography variant="subtitle2" color="primary" style={{ fontWeight: 700, marginBottom: 6 }}>
          {i18n.t("flowBuilder.interactiveButtons.quickReplyButtons")} ({buttons.length}/3)
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
          {i18n.t("flowBuilder.interactiveButtons.quickReplyDesc")}
        </Typography>

        {buttons.map((btn, idx) => (
          <Box key={idx} style={{ marginBottom: 12 }}>
            <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <TextField
                label={i18n.t("flowBuilder.interactiveButtons.buttonLabel", { index: idx + 1 })}
                value={btn.text}
                onChange={e => updateButton(idx, e.target.value)}
                variant="outlined"
                margin="dense"
                fullWidth
                inputProps={{ maxLength: 20 }}
                helperText={`${(btn.text || "").length}/20 — Handle: btn-${btn.id || `btn_${idx + 1}`}`}
              />
              {buttons.length > 1 && (
                <IconButton size="small" onClick={() => removeButton(idx)} color="secondary">
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Box>
            <Box style={{ display: "flex", gap: 8 }}>
              <TextField
                label={i18n.t("flowBuilder.interactiveButtons.varName", "Variable (opcional)")}
                value={btn.varName || ""}
                onChange={e => updateButtonVar(idx, "varName", e.target.value)}
                variant="outlined"
                margin="dense"
                size="small"
                style={{ flex: 1 }}
                inputProps={{ style: { fontSize: '0.8rem' } }}
                helperText={i18n.t("flowBuilder.interactiveButtons.varNameHelper", "Nombre variable")}
              />
              <TextField
                label={i18n.t("flowBuilder.interactiveButtons.varValue", "Valor a guardar")}
                value={btn.varValue || ""}
                onChange={e => updateButtonVar(idx, "varValue", e.target.value)}
                variant="outlined"
                margin="dense"
                size="small"
                style={{ flex: 2 }}
                inputProps={{ style: { fontSize: '0.8rem' } }}
                helperText={i18n.t("flowBuilder.interactiveButtons.varValueHelper", "Se guarda al presionar")}
              />
            </Box>
          </Box>
        ))}

        {buttons.length < 3 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addButton}
            color="primary"
            style={{ marginBottom: 16 }}
          >
            {i18n.t("flowBuilder.interactiveButtons.addOption")}
          </Button>
        )}

        <Divider style={{ margin: "16px 0" }} />

        {/* CTA Buttons */}
        <Typography variant="subtitle2" color="secondary" style={{ fontWeight: 700, marginBottom: 4 }}>
          {i18n.t("flowBuilder.interactiveButtons.ctaTitle")}
        </Typography>
        <Box style={{
          border: "1px solid",
          borderColor: "currentColor",
          borderRadius: 6,
          padding: "8px 12px",
          marginBottom: 12
        }}>
          <Typography variant="caption" color="error">
            {i18n.t("flowBuilder.interactiveButtons.ctaWarning")}
          </Typography>
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              label={i18n.t("flowBuilder.interactiveButtons.urlLabel")}
              value={ctaUrl?.label || ""}
              onChange={e => {
                const val = e.target.value;
                setCtaUrl(p => ({ ...p, label: val.substring(0, 20) }));
              }}
              variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={i18n.t("flowBuilder.interactiveButtons.url")}
              value={ctaUrl?.url || ""}
              onChange={e => {
                const val = e.target.value;
                setCtaUrl(p => ({ ...p, url: val }));
              }}
              variant="outlined" margin="dense" fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={i18n.t("flowBuilder.interactiveButtons.phoneLabel")}
              value={ctaPhone?.label || ""}
              onChange={e => {
                const val = e.target.value;
                setCtaPhone(p => ({ ...p, label: val.substring(0, 20) }));
              }}
              variant="outlined" margin="dense" fullWidth inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={i18n.t("flowBuilder.interactiveButtons.phoneNumber")}
              value={ctaPhone?.phone || ""}
              onChange={e => {
                const val = e.target.value;
                setCtaPhone(p => ({ ...p, phone: val }));
              }}
              variant="outlined" margin="dense" fullWidth
            />
          </Grid>
        </Grid>
      {/* AntiBan Toggle */}
      <Box style={{
        marginTop: "20px",
        marginBottom: "8px",
        padding: "12px 16px",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        borderRadius: "8px",
        background: antiBan ? "rgba(74,222,128,0.07)" : "rgba(0,0,0,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Box>
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: antiBan ? "#4ade80" : "rgba(255,255,255,0.7)" }}>
            🛡️ AntiBan IA {antiBan ? "[Activado]" : "[Desactivado]"}
          </Typography>
          <Typography variant="caption" style={{ color: "rgba(255,255,255,0.5)" }}>
            {antiBan
              ? "La IA reescribirá el mensaje automáticamente para evitar detección de spam."
              : "El mensaje se enviará exactamente como fue escrito (asume el riesgo)."}
          </Typography>
        </Box>
        <Switch
          checked={antiBan}
          onChange={(e) => setAntiBan(e.target.checked)}
          color="primary"
          style={{ color: antiBan ? "#4ade80" : undefined }}
        />
      </Box>

      </DialogContent>
      <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid rgba(255, 255, 255, 0.12)" }}>
        <Button onClick={onClose} className={standardClasses.secondaryButton}>
          {i18n.t("flowBuilder.interactiveButtons.cancel")}
        </Button>
        <Button onClick={handleSave} className={standardClasses.applyButton}>
          {i18n.t("flowBuilder.interactiveButtons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderInteractiveButtonModal;
